import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Tooltip } from '@/shared/ui/tooltip';
import { useAiModels } from '../api/use-ai-admin';
import { formatUsd, modelLabel, parseUsdText, usdToText } from '../model/format';
import type {
  AiSettingField,
  AiSettingSource,
  AiSettings,
  AiSettingsPatch,
  EffectiveAiSettings,
} from '../model/types';
import { ModelPicker } from './model-picker';
import { UsdInput } from './usd-input';

type TriState = 'default' | 'on' | 'off';

function toTriState(value: boolean | null): TriState {
  if (value === null) return 'default';
  return value ? 'on' : 'off';
}

function fromTriState(value: TriState): boolean | null {
  if (value === 'default') return null;
  return value === 'on';
}

function FieldRow({
  label,
  help,
  inherited,
  children,
}: {
  label: string;
  help: string;
  inherited?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Label>{label}</Label>
          <Tooltip content={help} />
        </div>
        {inherited}
      </div>
      {children}
    </div>
  );
}

interface AiSettingsFormProps {
  /** Nível editado. */
  level: Exclude<AiSettingSource, 'global'>;
  /** Overrides salvos neste nível (`null` = herda). */
  value: AiSettings;
  /** Valor efetivo e origem — mostra o que vale quando o campo herda. */
  inherited: EffectiveAiSettings | undefined;
  /** Pode rejeitar: o erro é tratado (toast) por quem chama. */
  onSave: (patch: AiSettingsPatch) => Promise<unknown>;
  saving?: boolean;
}

/** Configuração de IA em cascata: cada campo pode herdar ou sobrescrever o nível de cima. */
export function AiSettingsForm({
  value,
  inherited,
  onSave,
  saving,
}: AiSettingsFormProps) {
  const { data: catalog } = useAiModels();
  const [draft, setDraft] = useState<AiSettings>(value);
  const [limitText, setLimitText] = useState(usdToText(value.monthlyLimitUsd));

  useEffect(() => {
    setDraft(value);
    setLimitText(usdToText(value.monthlyLimitUsd));
  }, [value]);

  const parsedLimit = parseUsdText(limitText);
  const limitInvalid = parsedLimit === undefined;

  const patch = useMemo<AiSettingsPatch>(() => {
    const next: AiSettings = {
      ...draft,
      monthlyLimitUsd: parsedLimit === undefined ? value.monthlyLimitUsd : parsedLimit,
    };
    const changed: AiSettingsPatch = {};
    for (const field of Object.keys(next) as AiSettingField[]) {
      if (next[field] !== value[field]) Object.assign(changed, { [field]: next[field] });
    }
    return changed;
  }, [draft, parsedLimit, value]);

  const dirty = Object.keys(patch).length > 0;

  const ownBadge = (field: AiSettingField) => {
    const custom = field === 'monthlyLimitUsd' ? typeof parsedLimit === 'number' : draft[field] !== null;
    return custom ? (
      <Badge variant="accent" className="font-normal">
        própria
      </Badge>
    ) : null;
  };

  const values = inherited?.values;

  return (
    <div className="space-y-5">
      <FieldRow
        label="Modelo de IA"
        help="Modelo GPT-5.6 da OpenAI usado pelo assistente de segmentação. O custo de cada conversa é registrado."
        inherited={ownBadge('model')}
      >
        <ModelPicker
          value={draft.model}
          onChange={(model) => setDraft((d) => ({ ...d, model }))}
          defaultLabel={values ? modelLabel(values.model, catalog?.models) : 'nível de cima'}
        />
      </FieldRow>

      <FieldRow
        label="Limite mensal (USD)"
        help="Quanto cada usuário pode gastar. O usuário só vê o percentual usado. Deixe vazio para usar o padrão; 0 bloqueia o uso."
        inherited={ownBadge('monthlyLimitUsd')}
      >
        <UsdInput
          value={limitText}
          onValueChange={setLimitText}
          placeholder={values ? `Padrão (${formatUsd(values.monthlyLimitUsd)})` : 'Padrão'}
          aria-invalid={limitInvalid}
        />
        {limitInvalid ? <p className="text-xs text-destructive">Informe um valor maior ou igual a zero.</p> : null}
      </FieldRow>

      <FieldRow
        label="Renovação mensal"
        help="Com renovação, o consumo zera todo dia 1º. Sem renovação, o limite vira um saldo que só volta quando o master zera o uso."
        inherited={ownBadge('renewMonthly')}
      >
        <Select
          value={toTriState(draft.renewMonthly)}
          onValueChange={(next) => setDraft((d) => ({ ...d, renewMonthly: fromTriState(next as TriState) }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">
              {values ? `Padrão (${values.renewMonthly ? 'renova todo mês' : 'não renova'})` : 'Padrão'}
            </SelectItem>
            <SelectItem value="on">Renova todo mês</SelectItem>
            <SelectItem value="off">Não renova</SelectItem>
          </SelectContent>
        </Select>
      </FieldRow>

      <div className="flex justify-end gap-2 pt-1">
        <Button
          variant="ghost"
          disabled={!dirty || saving}
          onClick={() => {
            setDraft(value);
            setLimitText(usdToText(value.monthlyLimitUsd));
          }}
        >
          Descartar
        </Button>
        <Button
          disabled={!dirty || limitInvalid}
          loading={saving}
          onClick={() => void onSave(patch).catch(() => undefined)}
        >
          Salvar
        </Button>
      </div>
    </div>
  );
}
